import type { JSX } from 'react'
import { useMemo } from 'react'
import type { AttributeDefinition, GarmentCard } from '@fitting-room/contracts'
import { attributeBag, formatAttributeValue } from '../lib/attributes'
import { formatMoney } from '../lib/format'
import { uiStrings } from '../lib/strings'
import { text } from '../lib/text'

/**
 * What a garment says about itself, in words: its name, its price where a
 * boutique shows one, whichever of its attributes the tenant marks
 * `customerVisible`, and the sizes there are to try.
 *
 * It was the lower half of `GarmentCardFace` until the deck stopped being a
 * card. Two screens draw it inside a bordered frame — the shortlist and the
 * summary — and the feed draws it over a photograph, on the one tap that asks
 * for it. Extracted rather than copied because the rule it keeps is the same
 * on all three: which rows appear is the tenant's own `customerVisible` flags,
 * and this file has no idea which of them is a shape, a fabric or a colour.
 */
export function GarmentFacts({
  card,
  attributes,
  locale,
  /** `card` is a bordered card with a fixed height; `panel` is over a photo. */
  variant = 'card',
  compact = false,
}: {
  readonly card: GarmentCard
  readonly attributes: readonly AttributeDefinition[]
  readonly locale: string
  readonly variant?: 'card' | 'panel'
  readonly compact?: boolean
}): JSX.Element {
  const t = uiStrings(locale)
  const { garment, samples } = card
  const bag = useMemo(() => attributeBag(garment), [garment])

  const visible = attributes
    .filter((def) => def.customerVisible && bag.get(def.key) !== undefined)
    .map((def) => ({ def, value: formatAttributeValue(def, bag.get(def.key), locale) }))
    .filter((row) => row.value !== '')

  const sizes = [...new Set(samples.map((s) => s.size))]
  const name = text(garment.name, locale)
  const panel = variant === 'panel'

  return (
    <>
      <div className="flex shrink-0 items-baseline justify-between gap-3">
        {/*
          The heading level is the caller's layout, not this component's
          judgement: a compact card sits inside a slot section that already
          owns an h2, and the feed's panel sits on a screen whose h1 is in the
          header. Both of those are `h3`; a card standing on its own is `h2`.
        */}
        {compact || panel ? (
          <h3 className={`fr-display leading-tight ${panel ? 'text-[1.625rem]' : 'text-[1.125rem]'}`}>{name}</h3>
        ) : (
          <h2 className="fr-display text-[1.375rem] leading-tight">{name}</h2>
        )}
        {garment.price ? (
          <span className="shrink-0 text-[0.8125rem] tabular-nums" style={{ color: 'var(--fr-ink-dim)' }}>
            {formatMoney(garment.price, locale)}
          </span>
        ) : null}
      </div>

      {/*
        One column when the card is compact. Two of these sit side by side on
        a 390px phone, which leaves ~80px a column — enough to turn every
        label and value into an ellipsis. "Sweethe…" is not a neckline.
      */}
      {visible.length > 0 ? (
        <dl className={compact ? 'grid min-h-0 grid-cols-1 gap-y-1 overflow-hidden' : 'grid min-h-0 grid-cols-2 gap-x-3 gap-y-1 overflow-hidden'}>
          {visible.map(({ def, value }) => (
            <div key={def.key} className="min-w-0">
              <dt className="fr-eyebrow truncate" style={{ fontSize: '0.6875rem', letterSpacing: '0.2em' }}>
                {text(def.label, locale)}
              </dt>
              {/*
                Two lines, then the ellipsis. A multi-select value is a list —
                "Lace · Beading · Basque waist" — and on one line it came out
                as "Lace · Beadi…", which names one detail of three and tells
                her nothing about the gown. The LABEL above stays on one line:
                it is a single word and wrapping it would only cost a row.
              */}
              <dd className="line-clamp-2 text-[0.8125rem] leading-snug" style={{ color: 'var(--fr-ink)' }}>
                {value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {/*
        Sizes, and only where sizes are a fact.
        The rail is physical pieces, so it can say "6, 10" and mean it. Made
        to order has no sizes to report — it is cut for her — and this line
        used to fill that silence with "Not on the floor here", which was a
        claim about a sample register the shop does not keep. A gown she can
        try, captioned to say she cannot.
      */}
      {sizes.length > 0 ? (
        <div className={`shrink-0 border-t pt-2 ${panel ? '' : 'mt-auto'}`} style={{ borderColor: 'var(--fr-rule)' }}>
          <span className="block truncate text-[0.75rem]" style={{ color: 'var(--fr-ink-faint)' }}>
            {`${t.sampleSizes} · ${sizes.join(', ')}`}
          </span>
        </div>
      ) : null}
    </>
  )
}
